import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { ChartBarIcon, CurrencyDollarIcon, UserGroupIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { SEOMeta } from '@/templates/SEOMeta';
import { getI18nProps } from '@/utils/i18n';

export default function About({ seoMeta }) {
  const router = useRouter();
  const t = useTranslations('about');

  const stats = [
    { value: t('stats.activePartnerships.value'), label: t('stats.activePartnerships.label') },
    { value: t('stats.supportedCurrencies.value'), label: t('stats.supportedCurrencies.label') },
    { value: t('stats.uptimeReliability.value'), label: t('stats.uptimeReliability.label') }
  ];


  const values = [
    { icon: CurrencyDollarIcon, title: t('values.fairMonetization.title'), text: t('values.fairMonetization.text') },
    { icon: UserGroupIcon, title: t('values.collaborationFirst.title'), text: t('values.collaborationFirst.text') },
    { icon: SparklesIcon, title: t('values.innovationDriven.title'), text: t('values.innovationDriven.text') },
    { icon: ChartBarIcon, title: t('values.dataTransparency.title'), text: t('values.dataTransparency.text') }
  ];

  return (
      <>
        <SEOMeta
            title={seoMeta?.title || t('seo.title')}
            description={seoMeta?.description || t('seo.description')}
            keywords={seoMeta?.keywords || t('seo.keywords')}
            url={seoMeta?.url || "/about"}
        />

        <div className="bg-gradient-to-b from-gray-50 to-white">
          {/* Hero Section */}
          <section className="pt-28 pb-24 px-6">
            <div className="max-w-7xl mx-auto">
              <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center space-y-8"
              >
                <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                  {t('hero.title')}
                </h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  {t('hero.subtitle')}
                </p>
              </motion.div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                {stats.map((stat, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        className="text-center p-6 border rounded-xl"
                    >
                      <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
                        {stat.value}
                      </div>
                      <div className="text-gray-600 mt-2">{stat.label}</div>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-24 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold mb-4">{t('principles.title')}</h2>
                <p className="text-gray-600 max-w-xl mx-auto">
                  {t('principles.subtitle')}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {values.map((value, idx) => (
                    <motion.div
                        key={idx}
                        whileHover={{ y: -10 }}
                        className="p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-all"
                    >
                      <value.icon className="w-12 h-12 text-blue-600 mb-6" />
                      <h3 className="text-2xl font-semibold mb-4">{value.title}</h3>
                      <p className="text-gray-600">{value.text}</p>
                    </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-24">
            <div className="max-w-7xl mx-auto px-6 text-center">
              <motion.div
                  initial={{ scale: 0.95 }}
                  whileInView={{ scale: 1 }}
                  className="bg-gradient-to-r from-blue-600 to-green-500 p-12 rounded-3xl text-white"
              >
                <h2 className="text-4xl font-bold mb-6">{t('cta.title')}</h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto">
                  {t('cta.text')}
                </p>
                <div className="flex justify-center gap-4">
                  <Button green onClick={() => router.push('/signup')}>
                    {t('cta.business')}
                  </Button>
                  <Button
                      blue
                      variant="outline"
                      onClick={() => router.push(`${process.env.NEXT_PUBLIC_AFFILIATE_SITE_URL}/signup`)}
                  >
                    {t('cta.creators')}
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>
        </div>
      </>
  );
}

export async function getStaticProps(ctx) {
  return getI18nProps(ctx);
}
